import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Button, Box, Grid, MenuItem, FormControl, TextField, Autocomplete,
} from '@mui/material';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';
// import { DESIGNATIONS } from '../../constants/designation';
import DatePicker from '../../components/datePicker';
import ProjectSelector from '../../components/selectors/ProjectSelector';

// Convert employment start date to workspace timezone
const getInitialEmploymentStartDate = (employmentStartDate) => {
  if (!employmentStartDate) return new Date();
  // Convert the date to the workspace timezone for correct display
  // const timezone = workspaceTimezone || 'UTC';
  return moment(employmentStartDate).toDate();
};

function EditMemberModal({
  member, closeModal, getMembers, reportingManagers, departments, designations,
}) {
  const { activeWorkspace } = useContext(UserContext);

  const [formData, setFormData] = useState({
    firstName: member.user.firstName,
    lastName: member.user.lastName,
    email: member.user.email,
    timezone: member.user.timezone,
    accountType: member.accountType,
    department: member.department,
    designation: member.designation,
    employmentStartDate: getInitialEmploymentStartDate(
      member.employmentStartDate,
      activeWorkspace?.timezone,
    ),
    latlng: {
      latitude: member.user.userLocation?.latitude,
      longitude: member.user.userLocation?.longitude,
    },
    location: {
      description: member.user.userLocation?.formattedAddress,
    },
    reportingManager: member.reportingManager,
  });
  const [successMessage, setSuccessMessage] = useState();
  const [errorMessage, setErrorMessage] = useState();
  // eslint-disable-next-line
  const [formErrors, setFormErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [memberProjects, setMemberProjects] = useState([]);

  const isUpdatable = () => {
    const originalProjectIds = (memberProjects || []).map((mp) => mp.projectId).sort();
    const selectedProjectIds = (formData.selectedProjects || []).map((p) => p.id).sort();
    const projectsChanged = JSON.stringify(originalProjectIds) !== JSON.stringify(selectedProjectIds);

    // Compare dates by converting formData date to string for comparison
    const formDataDateStr = formData.employmentStartDate
      ? moment(formData.employmentStartDate).format('YYYY-MM-DD')
      : null;
    const memberDateStr = member.employmentStartDate
      ? moment(member.employmentStartDate).format('YYYY-MM-DD')
      : null;
    const dateChanged = formDataDateStr !== memberDateStr;

    return formData.accountType !== member.accountType
      || formData.department !== member.department
      || formData.designation !== member.designation
      || formData.latlng?.latitude !== member.user.userLocation?.latitude
      || formData.latlng?.longitude !== member.user.userLocation?.longitude
      || formData.location?.description !== member.user.userLocation?.formattedAddress
      || formData.reportingManager !== member.reportingManager
      || dateChanged
      || projectsChanged;
  };

  const handleForm = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProjectSelection = (selectedProjects) => {
    setFormData({ ...formData, selectedProjects });
  };

  const handleEditMember = async () => {
    setFormErrors({});
    setIsUpdating(true);

    try {
      const updateData = {
        email: typeof formData.email === 'string' ? formData.email.trim() : formData.email,
        accountType: typeof formData.accountType === 'string'
          ? formData.accountType.trim() : formData.accountType,
        department: typeof formData.department === 'string'
          ? (formData.department.trim() || null) : (formData.department || null),
        designation: typeof formData.designation === 'string'
          ? (formData.designation.trim() || null) : (formData.designation || null),
        reportingManager: formData.reportingManager || null,
        employmentStartDate: formData.employmentStartDate,
        latlng: formData.latlng,
        location: formData.location,
      };

      const memberResponse = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.editMember(activeWorkspace.id, member.id),
        data: { updateData },
      });

      if (memberResponse.success) {
        try {
          const selectedProjectIds = (formData.selectedProjects || []).map((project) => project.id);

          const projectUpdateResponse = await sendRequest({
            method: 'put',
            url: API.WORKSPACE.updateMemberProjects(activeWorkspace.id, member.id),
            data: { projectIds: selectedProjectIds },
          });

          if (projectUpdateResponse.success) {
            const totalProjects = selectedProjectIds.length;
            const message = totalProjects > 0
              ? `Member updated and assigned to ${totalProjects} project(s) successfully!`
              : 'Member updated successfully!';

            setSuccessMessage(message);

            await getMembers();

            closeModal();
          } else {
            setErrorMessage('Failed to update member projects. Please try again.');
          }
        } catch (projectError) {
          setErrorMessage('Failed to update member projects. Please try again.');
        }
      } else {
        setErrorMessage('Failed to update member. Please try again.');
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const loadProjectsAndMemberProjects = async () => {
      if (activeWorkspace && member) {
        setProjectsLoading(true);

        try {
          const projectsResponse = await sendRequest({
            method: 'get',
            url: API.WORKSPACE.getProjects(activeWorkspace.id),
          });

          let allProjects = [];
          if (projectsResponse.success && projectsResponse.data?.data
            && Array.isArray(projectsResponse.data.data)) {
            allProjects = projectsResponse.data.data.map((project) => ({
              id: project.id,
              name: project.name || `Project ${project.id}`,
              description: project.description || '',
              workspaceId: project.workspaceId,
              ...project,
            }));
          }

          setProjects(allProjects);

          try {
            const memberProjectsResponse = await sendRequest({
              method: 'get',
              url: API.WORKSPACE.getMemberProjects(activeWorkspace.id, member.id),
            });

            if (memberProjectsResponse.success && memberProjectsResponse.data.data) {
              const currentMemberProjects = Array.isArray(memberProjectsResponse.data.data)
                ? memberProjectsResponse.data.data
                : [];

              setMemberProjects(currentMemberProjects);

              const selectedProjects = [];

              currentMemberProjects.forEach((memberProject) => {
                const matchingProject = allProjects.find((project) => project.id === memberProject.projectId);

                if (matchingProject) {
                  selectedProjects.push(matchingProject);
                } else {
                  console.warn('Could not find matching project for projectId:', memberProject.projectId);
                }
              });

              setFormData((prev) => ({
                ...prev,
                selectedProjects,
              }));
            } else {
              setMemberProjects([]);
              setFormData((prev) => ({ ...prev, selectedProjects: [] }));
            }
          } catch (memberProjectError) {
            setMemberProjects([]);
            setFormData((prev) => ({ ...prev, selectedProjects: [] }));
          }
        } catch (error) {
          setProjects([]);
        } finally {
          setProjectsLoading(false);
        }
      }
    };

    loadProjectsAndMemberProjects();
  }, [activeWorkspace, member]);

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c" data-testid="edit-member-modal">
      <Box style={{
        width: 482,
        padding: 32,
        background: '#fff',
        borderRadius: 8,
      }}
      >
        <h5 className="m-b16 text-600" data-testid="modal-title">Edit Member</h5>
        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                name="firstName"
                label="First Name"
                variant="standard"
                value={formData?.firstName}
                type="text"
                disabled
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                name="lastName"
                label="Last Name"
                variant="standard"
                value={formData?.lastName}
                type="text"
                disabled
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                name="email"
                label="Email Address"
                variant="standard"
                value={formData?.email}
                type="email"
                disabled
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                select
                name="accountType"
                value={formData.accountType}
                label="Account Type"
                onChange={handleForm}
                disabled={formData.accountType === 'Primary owner'}
                variant="standard"
              >
                <MenuItem
                  value="Primary owner"
                  hidden={formData.accountType !== 'Primary owner'}
                >
                  Primary owner
                </MenuItem>
                <MenuItem value="Team member">Team member</MenuItem>
                <MenuItem value="Team Leader">Team Leader</MenuItem>
                <MenuItem value="Workspace owner">Workspace owner</MenuItem>
                <MenuItem value="Workspace admin">Workspace admin</MenuItem>
              </TextField>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12}> */}
          {/*  <FormControl fullWidth> */}
          {/*    <TextField */}
          {/*      name="timezone" */}
          {/*      label="Timezone" */}
          {/*      variant="standard" */}
          {/*      value={formData?.timezone} */}
          {/*      type="text" */}
          {/*      disabled */}
          {/*    /> */}
          {/*  </FormControl> */}
          {/* </Grid> */}
          <Grid item xs={12}>
            <FormControl sx={{ width: '100%' }}>
              <Autocomplete
                value={
                    reportingManagers.find((m) => m.id === formData.reportingManager) || null
                  }
                onChange={(e, newValue) => {
                  setFormData({ ...formData, reportingManager: newValue?.id || '' });
                }}
                options={reportingManagers.filter((m) => m.id !== member.id)}
                getOptionLabel={(option) => (`${option.user.firstName} ${option.user.lastName}`) || ''}
                renderInput={(params) => (
                  <TextField
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...params}
                    label="Reporting To"
                    variant="standard"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                select
                name="department"
                value={formData.department}
                label="Department"
                onChange={handleForm}
                variant="standard"
                data-testid="department"
              >
                {
                  departments.map((depratment) => (
                    <MenuItem value={depratment.name}>{depratment.name}</MenuItem>
                  ))
                }
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                select
                name="designation"
                value={formData.designation}
                label="Designation"
                onChange={handleForm}
                variant="standard"
                data-testid="designation"
              >
                {
                  designations.map((designation) => (
                    <MenuItem value={designation.name}>{designation.name}</MenuItem>
                  ))
                }
              </TextField>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <ProjectSelector
                label="Projects"
                options={projects}
                selected={formData.selectedProjects}
                onChange={handleProjectSelection}
                placeholder={projects.length === 0 ? 'No projects available' : 'Select projects...'}
                loading={projectsLoading}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="employment-start-date-label">
                Employment Start Date
              </label>
              <DatePicker
                oneTap
                format="MM/dd/yyyy"
                name="employmentStartDate"
                value={formData.employmentStartDate}
                onChange={(_date) => setFormData({ ...formData, employmentStartDate: _date })}
                className="employment-start-date-picker"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <PlacesAutocomplete
              location={formData.location}
              onChange={setFormData}
              textfieldType="standard"
              label="Home Address"
              placeholder="Enter Street Address"
            />
          </Grid>
        </Grid>
        <div className="newMemberForm-btns" style={{ marginTop: 24 }}>
          <Button
            onClick={closeModal}
            color="secondary"
            sx={{ marginRight: 2 }}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditMember}
            disabled={!isUpdatable() || isUpdating}
            data-testid="update-member"
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </div>
        <div className="success-msg">{successMessage}</div>
        <div className="error-msg">{errorMessage}</div>
      </Box>
    </Modal>
  );
}

EditMemberModal.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    accountType: PropTypes.string,
    department: PropTypes.string,
    designation: PropTypes.string,
    reportingManager: PropTypes.number,
    employmentStartDate: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      timezone: PropTypes.string,
      userLocation: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        formattedAddress: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  reportingManagers: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  departments: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  designations: PropTypes.array.isRequired,
};

export default EditMemberModal;
